import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

type PaginationProps = {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevious: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  totalCount,
  hasNextPage,
  hasPrevious,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious}
          className="p-2 rounded-md disabled:opacity-50 border border-[#F1F2F4]"
        >
          <FiChevronLeft />
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i + 1}
            onClick={() => onPageChange(i + 1)}
            className={`px-3 py-1 rounded-md font-normal text-sm ${
              currentPage === i + 1 ? 'bg-[#F8F8F8] text-[#111827]' : 'bg-none'
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className="p-2 rounded-md disabled:opacity-50 border border-[#F1F2F4]"
        >
          <FiChevronRight />
        </button>
      </div>

      <div className="flex gap-4 items-center">
        <p className="text-[#687588] font-normal">
          Showing <span>{(currentPage - 1) * pageSize + 1}</span> to{' '}
          <span>{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
          <span>{totalCount}</span> entries
        </p>

        <div className="border border-[#F1F2F4] rounded-xl flex gap-2 items-center w-fit px-2 py-1 text-sm">
          <label htmlFor="pageSize" className="text-[#111827] font-normal">
            Show
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              onPageChange(1); // Reset to first page
              onPageSizeChange(Number(e.target.value));
            }}
            className="bg-transparent outline-none text-[#111827] font-normal"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
